import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Effect } from '@babylonjs/core/Materials/effect';
import { getRuntimeProfile } from '../core/RuntimeProfile';

let shadowGenerator: CascadedShadowGenerator;

export function setupScene(scene: Scene): CascadedShadowGenerator {
  const profile = getRuntimeProfile();

  // ── Digital void — neutral black, no outdoor atmosphere ──
  scene.clearColor = new Color4(0.004, 0.005, 0.007, 1.0);
  scene.ambientColor = new Color3(0.035, 0.035, 0.04);

  // Reduced fog density — can see shots landing at 300+ yd
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = 0.0018;
  scene.fogColor = new Color3(0.004, 0.005, 0.007);

  // ── Key light — strong directional for dramatic shadows ──
  const keyLight = new DirectionalLight('studio-key', new Vector3(-0.4, -0.8, 0.6).normalize(), scene);
  keyLight.intensity = 2.0;
  keyLight.diffuse = new Color3(0.94, 0.97, 1.0);
  keyLight.specular = new Color3(1, 1, 1);

  // ── Fill light — subtle cool tone ──
  const hemiLight = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  hemiLight.intensity = 0.65;
  hemiLight.diffuse = new Color3(0.50, 0.57, 0.66);
  hemiLight.groundColor = new Color3(0.035, 0.03, 0.055);

  // ── Cascaded Shadow Map ──
  shadowGenerator = new CascadedShadowGenerator(profile.shadowMapSize, keyLight);
  shadowGenerator.numCascades = profile.shadowCascades;
  shadowGenerator.lambda = 0.7;
  shadowGenerator.stabilizeCascades = true;
  shadowGenerator.shadowMaxZ = 400;
  shadowGenerator.depthClamp = true;
  shadowGenerator.autoCalcDepthBounds = true;
  shadowGenerator.bias = 0.001;
  shadowGenerator.normalBias = 0.02;
  shadowGenerator.filteringQuality = profile.isMobile
    ? CascadedShadowGenerator.QUALITY_MEDIUM
    : CascadedShadowGenerator.QUALITY_HIGH;

  // ── Background — a barely visible neutral horizon, no sun or landscape ──
  setupSkybox(scene);

  return shadowGenerator;
}

function setupSkybox(scene: Scene): void {
  // Static digital backdrop: no asset download, animation, or per-frame CPU work.
  Effect.ShadersStore['rangeSkyVertexShader'] = `
    precision highp float;
    attribute vec3 position;
    uniform mat4 worldViewProjection;
    varying vec3 direction;
    void main() { direction = position; gl_Position = worldViewProjection * vec4(position, 1.0); }
  `;
  Effect.ShadersStore['rangeSkyFragmentShader'] = `
    precision highp float;
    varying vec3 direction;
    void main() {
      vec3 d = normalize(direction);
      float horizon = exp(-abs(d.y) * 14.0);
      vec3 color = vec3(0.002, 0.003, 0.005) + vec3(0.008, 0.010, 0.018) * horizon;
      gl_FragColor = vec4(color, 1.0);
    }
  `;
  const skybox = MeshBuilder.CreateBox('skyBox', { size: 1000 }, scene);
  const skyMat = new ShaderMaterial('skyBoxMat', scene, 'rangeSky', {
    attributes: ['position'], uniforms: ['worldViewProjection'],
  });
  skyMat.backFaceCulling = false;
  skyMat.disableDepthWrite = true;
  skybox.isPickable = false;
  skybox.material = skyMat;
  skybox.infiniteDistance = true;
  skybox.renderingGroupId = 0;
}

export function getShadowGenerator(): CascadedShadowGenerator {
  return shadowGenerator;
}
